<template>
  <div>
    <!-- Filters -->
    <vx-card
      :title="$t('Filters')"
      class="mb-8"
      collapse-action
    >
      <div class="vx-row">

        <!-- Search -->
        <div class="vx-col md:w-1/4 w-full">
          <label class="text-sm opacity-75">
            {{ $t("Search") }}
          </label>

          <vs-input
            v-model="filters.keySearch"
            :placeholder="$t('Code_ID_Iqama')"
            class="w-full mt-2"
          />
        </div>

        <!-- Nationality -->
        <div class="vx-col md:w-1/4 w-full">
          <label class="text-sm opacity-75">
            {{ $t("Nationality") }}
          </label>

          <vs-select
            v-model="filters.NationalityId"
            class="w-full mt-2"
          >
            <vs-select-item
              v-for="item in Nationalities"
              :key="item.Id"
              :value="item.Id"
              :text="
                currentLocale === 'ar'
                  ? item.NameAr
                  : item.NameEn
              "
            />
          </vs-select>
        </div>

      </div>

      <!-- Reset button -->
      <div class="vx-row mt-4">
        <div class="vx-col w-full flex justify-end">
          <vs-button
            color="danger"
            type="border"
            @click="resetAll"
          >
            {{ $t("ResetSearch") }}
          </vs-button>
        </div>
      </div>
    </vx-card>

    <!-- Property Owners Table -->
    <div
      id="data-list-list-view"
      class="data-list-container property-owners-table"
    >
      <vs-table
        pagination
        search
        :max-items="itemsPerPage"
        :data="PropertyOwners"
      >
        <!-- Table header area -->
        <template slot="header">
          <div
            class="
              flex
              flex-wrap-reverse
              items-center
              flex-grow
              justify-between
            "
          >
            <vs-button
              type="border"
              icon-pack="feather"
              icon="icon-plus"
              @click="AddOrEditOwner({ Id: 0 })"
            >
              {{ $t("AddNewOwner") }}
            </vs-button>
          </div>
        </template>

        <!-- Column headings -->
        <template slot="thead">
          <vs-th sort-key="OwnerCode">
            {{ $t("Code") }}
          </vs-th>

          <vs-th sort-key="FullName">
            {{ $t("Name") }}
          </vs-th>

          <vs-th sort-key="NationalIdOrIqama">
            {{ $t("NationalID_Iqama") }}
          </vs-th>

          <vs-th>
            {{ $t("Actions") }}
          </vs-th>
        </template>

        <!-- Table rows -->
        <template slot-scope="{ data }">
          <tbody>
            <tr
              v-for="tr in data"
              :key="tr.Id"
              class="cursor-pointer"
              @click="AddOrEditOwner(tr)"
            >
              <!-- Owner Code -->
              <vs-td>
                {{ tr.OwnerCode || "-" }}
              </vs-td>

              <!-- Owner Name -->
              <vs-td>
                {{ getOwnerName(tr) }}
              </vs-td>

              <!-- National ID / Iqama -->
              <vs-td>
                {{ tr.NationalIdOrIqama || "-" }}
              </vs-td>

              <!-- Actions -->
              <vs-td class="whitespace-no-wrap">
                <div class="flex items-center">

                  <!-- Edit -->
                  <vx-tooltip :text="$t('Edit')">
                    <feather-icon
                      icon="EditIcon"
                      svg-classes="
                        w-5
                        h-5
                        hover:text-primary
                        cursor-pointer
                      "
                      @click.stop="AddOrEditOwner(tr)"
                    />
                  </vx-tooltip>

                  <!-- Activate / Block -->
                  <vx-tooltip
                    :text="
                      tr.IsActive
                        ? $t('Block')
                        : $t('Activate')
                    "
                    class="ml-2 rtl:mr-2"
                  >
                    <feather-icon
                      :icon="
                        tr.IsActive
                          ? 'UserXIcon'
                          : 'CheckCircleIcon'
                      "
                      :svg-classes="[
                        'w-5 h-5 cursor-pointer',
                        tr.IsActive
                          ? 'hover:text-danger'
                          : 'hover:text-success'
                      ]"
                      @click.stop="toggleActivation(tr)"
                    />
                  </vx-tooltip>

                </div>
              </vs-td>
            </tr>
          </tbody>
        </template>
      </vs-table>
    </div>
  </div>
</template>

<script>
import VxCard from "../../components/vx-card/VxCard.vue";

import modulePropertyOwners from "@/store/PropertyOwners/modulePropertyOwners.js";
import moduleNationalities from "@/store/Nationality/moduleNationalities.js";

export default {
  name: "PropertyOwners",

  components: {
    VxCard
  },

  data() {
    return {
      filters: {
        keySearch: "",
        NationalityId: null
      },

      itemsPerPage: 10,

      searchTimer: null
    };
  },

  computed: {
    currentLocale() {
      return this.$i18n.locale;
    },

    PropertyOwners() {
      const storeModule =
        this.$store.state.PropertyOwnersList;

      if (!storeModule) {
        return [];
      }

      return storeModule.PropertyOwners || [];
    },

    Nationalities() {
      const storeModule =
        this.$store.state.NationalitiesList;

      if (!storeModule) {
        return [];
      }

      return storeModule.Nationalities || [];
    }
  },

  watch: {
    filters: {
      deep: true,

      handler() {
        this.debouncedSearch();
      }
    }
  },

  created() {
    this.registerStoreModules();
    this.loadInitialData();
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },

  methods: {
    /**
     * Register Vuex modules.
     */
    registerStoreModules() {
      if (!modulePropertyOwners.isRegistered) {
        this.$store.registerModule(
          "PropertyOwnersList",
          modulePropertyOwners
        );

        modulePropertyOwners.isRegistered = true;
      }

      if (!moduleNationalities.isRegistered) {
        this.$store.registerModule(
          "NationalitiesList",
          moduleNationalities
        );

        moduleNationalities.isRegistered = true;
      }
    },

    /**
     * Load initial table data and nationalities.
     */
    loadInitialData() {
      this.$store.dispatch(
        "PropertyOwnersList/fetchDataListItems"
      );

      this.$store.dispatch(
        "NationalitiesList/fetchDataListItems"
      );
    },

    /**
     * Get owner name depending on current language.
     */
    getOwnerName(owner) {
      if (!owner) {
        return "-";
      }

      if (this.currentLocale === "ar") {
        return (
          owner.FullNameAr ||
          owner.UserFullNameAr ||
          owner.FullNameEn ||
          owner.UserFullNameEn ||
          "-"
        );
      }

      return (
        owner.FullNameEn ||
        owner.UserFullNameEn ||
        owner.FullNameAr ||
        owner.UserFullNameAr ||
        "-"
      );
    },

    /**
     * Open add/edit owner page.
     */
    AddOrEditOwner(data = {}) {
      const id = data && data.Id
        ? data.Id
        : 0;

      this.$router.push(
        `/PropertyOwners/AddOrEditOwner/${id}`
      );
    },

    /**
     * Activate or block owner.
     */
    toggleActivation(owner) {
      const isActive = Boolean(owner.IsActive);

      const ownerName =
        owner.OwnerCode ||
        this.getOwnerName(owner);

      this.$vs.dialog({
        type: "confirm",

        color: isActive
          ? "danger"
          : "success",

        title: this.$t("Confirm"),

        text: `${this.$t("AreYouSureYouWantTo")} ${
          isActive
            ? this.$t("Block")
            : this.$t("Activate")
        } ${ownerName}?`,

        accept: () => {
          this.changeOwnerStatus(owner);
        }
      });
    },

    /**
     * Send owner status update request.
     */
    changeOwnerStatus(owner) {
      this.$vs.loading();

      this.$store
        .dispatch(
          "PropertyOwnersList/toggleOwnerStatus",
          owner.Id
        )
        .then((response) => {
          this.$vs.loading.close();

          const message =
            response &&
            response.data &&
            response.data.Message
              ? response.data.Message
              : this.$t("UpdatedSuccessfully");

          this.$vs.notify({
            title: this.$t("Success"),
            text: message,
            color: "success"
          });

          /*
           * Reload the table to guarantee that the
           * status icon reflects the database value.
           */
          return this.$store.dispatch(
            "PropertyOwnersList/fetchFilteredOwners",
            this.filters
          );
        })
        .catch((error) => {
          this.$vs.loading.close();

          console.error(
            "Failed to update property owner status:",
            error
          );

          this.$vs.notify({
            title: this.$t("Error"),
            text: this.$t("UpdateFailed"),
            color: "danger"
          });
        });
    },

    /**
     * Reset all filters.
     */
    resetAll() {
      this.filters = {
        keySearch: "",
        NationalityId: null
      };
    },

    /**
     * Delay search request while typing.
     */
    debouncedSearch() {
      clearTimeout(this.searchTimer);

      this.searchTimer = setTimeout(() => {
        this.applyFilters();
      }, 500);
    },

    /**
     * Fetch filtered owners.
     */
    applyFilters() {
      this.$store.dispatch(
        "PropertyOwnersList/fetchFilteredOwners",
        {
          keySearch: this.filters.keySearch,
          NationalityId: this.filters.NationalityId
        }
      );
    }
  }
};
</script>

<!--
  مهم:
  لا تضف scoped هنا لأن عناصر الجدول الداخلية
  يتم إنشاؤها داخل Vuesax component.
-->
<style lang="scss">
.property-owners-table {
  .vs-con-table {
    /*
     * Header containing Add button and Search input.
     */
    .vs-table--header {
      display: flex;
      flex-wrap: wrap-reverse;
      align-items: center;
      margin-left: 1.5rem;
      margin-right: 1.5rem;

      > span {
        display: flex;
        flex-grow: 1;
      }

      .vs-table--search {
        padding-top: 0;

        .vs-table--search-input {
          padding: 0.9rem 2.5rem;
          font-size: 1rem;

          & + i {
            left: 1rem;
          }

          &:focus + i {
            left: 1rem;
          }
        }
      }
    }

    /*
     * Main table.
     *
     * border-spacing creates the required space
     * between the header and the first table row.
     */
    .vs-table {
      width: 100%;
      border-collapse: separate !important;
      border-spacing: 0 1.3rem !important;
      padding: 0 1rem;

      tbody {
        tr {
          background-color: #ffffff;
          box-shadow:
            0 4px 20px 0
            rgba(0, 0, 0, 0.05);

          transition:
            transform 0.15s ease,
            box-shadow 0.15s ease;

          &:hover {
            transform: translateY(-1px);

            box-shadow:
              0 5px 22px 0
              rgba(0, 0, 0, 0.08);
          }

          td {
            padding: 20px;

            &:first-child {
              border-top-left-radius: 0.5rem;
              border-bottom-left-radius: 0.5rem;
            }

            &:last-child {
              border-top-right-radius: 0.5rem;
              border-bottom-right-radius: 0.5rem;
            }
          }

          td.td-check {
            padding: 20px !important;
          }
        }
      }
    }

    /*
     * Table headings.
     */
    .vs-table--thead {
      th {
        padding-top: 0;
        padding-bottom: 0;

        .vs-table-text {
          font-weight: 600;
          text-transform: uppercase;
        }
      }

      th.td-check {
        padding: 0 15px !important;
      }

      tr {
        background: none;
        box-shadow: none;
      }
    }

    /*
     * Center pagination.
     */
    .vs-table--pagination {
      justify-content: center;
    }
  }
}

/*
 * RTL adjustments.
 */
[dir="rtl"] {
  .property-owners-table {
    .vs-con-table {
      .vs-table {
        tbody {
          tr {
            td {
              &:first-child {
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
                border-top-right-radius: 0.5rem;
                border-bottom-right-radius: 0.5rem;
              }

              &:last-child {
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
                border-top-left-radius: 0.5rem;
                border-bottom-left-radius: 0.5rem;
              }
            }
          }
        }
      }

      .vs-table--header {
        .vs-table--search {
          .vs-table--search-input {
            padding-right: 2.5rem;
            padding-left: 0.9rem;

            & + i {
              right: 1rem;
              left: auto;
            }

            &:focus + i {
              right: 1rem;
              left: auto;
            }
          }
        }
      }
    }
  }
}
</style>